// The persisted document schemas — the four artifacts the app saves, kept
// deliberately distinct (issue #176, epic #177):
//
//   1. Score            — notation and semantic Feel cues (what the music IS)
//   2. TuneArrangement  — this tune's cue → groove assignments and overrides
//   3. GroovePreset     — a reusable catalogue template
//   4. (later) a personal preset: the same concept in a user's account
//
// Two rules make the difference between a system that ages well and one that
// rots, and both are encoded here rather than left to convention:
//
//   * STABLE OPAQUE IDS ARE THE KEYS. Not display titles. A tune renamed from
//     "Nica's Dream" to "Nicas Dream" must not orphan its arrangement, and two
//     tunes may legitimately share a title. (The current `tuneBands` is keyed
//     by lowercased "title composer" — precisely the bug this replaces.)
//
//   * PRESETS ARE COPIED, NEVER REFERENCED. Applying a groove copies a
//     versioned SNAPSHOT into the tune's arrangement. Editing the catalogue
//     preset later must never silently change a tune somebody already
//     arranged — the single most surprising thing a preset system can do.
//
// This module is the APP's entry point: it re-exports the whole schema core
// from `documentSchema.ts` and adds the one thing that core cannot have — a
// check of the music itself, which needs the score model. Import from here
// unless you are the dev writer's Vite plugin, which runs in plain Node.

import type { GrooveState } from '@/music/arrangement';
import type { Score } from '@/music/score/types';
import { validateScore } from '@/music/score/validate';
import {
  makeDocumentId,
  SCHEMA_VERSION,
  validateDocumentSchema,
  type GroovePresetDoc,
  type GrooveSnapshot,
  type StoredDocument,
  type TuneArrangementDoc,
  type ValidationIssue,
} from './documentSchema';

export * from './documentSchema';

/**
 * Structural validation at the trust boundary, INCLUDING the music.
 *
 * Anything read from disk or written by a client is untrusted: a malformed
 * document must be a reported error, never a half-applied load.
 */
export function validateDocument(doc: unknown): ValidationIssue[] {
  const issues = validateDocumentSchema(doc);
  if (!doc || typeof doc !== 'object') return issues;
  const d = doc as Partial<StoredDocument> & Record<string, unknown>;
  if (d.kind !== 'score') return issues;
  // The schema layer already reported a missing/non-object score; don't
  // report the same defect twice in different words.
  if (issues.some((issue) => issue.path === 'score')) return issues;
  return [...issues, ...scoreIssues(d.score).map((message) => ({ path: 'score', message }))];
}

export const MALFORMED_SCORE = 'score is missing its form or parts';

/**
 * Is this even a Score, structurally? Anything that fails here cannot be
 * normalized or validated — `normalizeScore` would throw a raw TypeError, and
 * "score.form.map is not a function" is not a report anyone can act on.
 * Callers that normalize before validating check this first.
 */
export function isScoreShaped(value: unknown): value is Score {
  if (!value || typeof value !== 'object') return false;
  const score = value as Partial<Score>;
  return Array.isArray(score.form) && Array.isArray(score.parts);
}

/**
 * The music itself, checked by the score model's OWN validator.
 *
 * This closes an asymmetry that was backwards: a Score arriving from a FILE
 * went through `parseNativeScoreDocument`, which normalizes and validates,
 * while a Score arriving from localStorage was accepted as long as it was an
 * object with a title. The untrusted path had the weaker check. One validator
 * now serves both, and "is this actually a Score" means the same thing
 * everywhere.
 *
 * Only `error` issues are fatal; warnings (an overfull bar, say) describe
 * music that is unusual, not data that is broken. `validateScore` walks the
 * structure and will throw on genuine garbage, so the shape is checked first
 * and the walk is guarded.
 */
function scoreIssues(value: unknown): string[] {
  if (!isScoreShaped(value)) return [MALFORMED_SCORE];
  const score = value as Partial<Score>;
  try {
    return validateScore(score as Score)
      .filter((issue) => issue.severity === 'error')
      .map((issue) => issue.message);
  } catch (error) {
    return [`score could not be read: ${error instanceof Error ? error.message : 'malformed'}`];
  }
}

// --- copy-on-apply ----------------------------------------------------------

/**
 * Apply a catalogue preset to a Feel in a tune — by COPY. The returned
 * arrangement carries the preset's state as its own; a later edit to the
 * preset cannot reach it. `from` is display provenance only.
 */
export function applyPresetToFeel(
  arrangement: TuneArrangementDoc,
  feel: string,
  preset: GroovePresetDoc,
): TuneArrangementDoc {
  return { ...arrangement, feels: { ...arrangement.feels, [feel]: snapshotOf(preset) } };
}

/** Apply a preset to ONE cue occurrence — the "this cue only" override. */
export function applyPresetToCue(
  arrangement: TuneArrangementDoc,
  changeId: string,
  preset: GroovePresetDoc,
): TuneArrangementDoc {
  return {
    ...arrangement,
    cueOverrides: { ...(arrangement.cueOverrides ?? {}), [changeId]: snapshotOf(preset) },
  };
}

function snapshotOf(preset: GroovePresetDoc): GrooveSnapshot {
  return {
    state: structuredClone(preset.state),
    from: {
      presetId: preset.id,
      name: preset.name,
      ...(preset.revisionId ? { revisionId: preset.revisionId } : {}),
    },
  };
}

/**
 * The groove in force for a cue: its own override if it has one, else the
 * Feel's assignment. Returns undefined when neither is assigned, so a caller
 * can fall back to the arrangement's base rather than guess.
 */
export function resolveCueGroove(
  arrangement: TuneArrangementDoc,
  feel: string,
  changeId?: string,
): GrooveState | undefined {
  if (changeId) {
    const override = arrangement.cueOverrides?.[changeId];
    if (override) return override.state;
  }
  return arrangement.feels[feel]?.state;
}

/**
 * Promote a tune's resolved groove into a NEW catalogue preset. A new
 * identity, not an edit of the source — so nothing that already uses the
 * original changes.
 */
export function promoteToPreset(
  state: GrooveState,
  name: string,
  seed: string,
  category?: string,
): GroovePresetDoc {
  return {
    kind: 'groove',
    id: makeDocumentId('groove', name, seed),
    schemaVersion: SCHEMA_VERSION,
    name,
    ...(category ? { category } : {}),
    state: structuredClone(state),
  };
}
